import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { ShoppingBag, ShoppingCart, Package, Search, ArrowLeft, User } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCourier } from '../../state/courierSlice';
import { getIsAuthenticated } from '../../state/authSlice';
import { useCart } from '../../context/CartContext';
import { useDarkMode } from '../../context/DarkModeContext';

const ShopLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const courier = useSelector(getCourier);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const { cartItems, getTotalItems } = useCart();
  const { isDarkMode } = useDarkMode();
  const totalItems = getTotalItems();

  const tabs = [
    { id: 'browse', label: 'Browse', icon: <ShoppingBag className="h-5 w-5" />, path: '/shop' },
    { id: 'cart', label: 'Cart', icon: <ShoppingCart className="h-5 w-5" />, path: '/shop/cart', badge: totalItems },
    ...(isAuthenticated ? [{ id: 'orders', label: 'Orders', icon: <Package className="h-5 w-5" />, path: '/shop/orders' }] : []),
  ];

  const getActiveTab = () => {
    if (location.pathname.includes('/shop/cart') || location.pathname.includes('/shop/checkout')) return 'cart';
    if (location.pathname.includes('/shop/orders')) return 'orders';
    return 'browse';
  };

  return (
    <div className={`flex flex-col h-[100dvh] ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      {/* Mobile Header */}
      <header className={`lg:hidden ${isDarkMode ? 'bg-gray-800' : 'bg-primary'} text-white shadow-md flex-shrink-0`}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <button
                onClick={() => navigate('/more')}
                className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h1 className="font-bold text-lg leading-tight">
                {courier?.name || 'Shop'}
              </h1>
              <p className="text-xs text-white/70">Shop</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/shop/cart')}
              className="relative p-2 hover:bg-white/10 rounded-full transition-colors text-white"
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <header className={`hidden lg:block ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b shadow-sm flex-shrink-0`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo/Name */}
            <Link to="/shop" className="flex items-center gap-3 min-h-[44px]">
              <div className={`w-9 h-9 rounded-lg ${isDarkMode ? 'bg-primary' : 'bg-primary'} flex items-center justify-center`}>
                <ShoppingBag className="h-5 w-5 text-white" />
              </div>
              <span className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {courier?.name || 'Shop'}
              </span>
            </Link>

            {/* Center: Navigation */}
            <nav className="flex items-center gap-1">
              {tabs.map((tab) => {
                const isActive = getActiveTab() === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => navigate(tab.path)}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px]
                      ${isActive
                        ? `${isDarkMode ? 'bg-primary/10 text-primary' : 'bg-primary/10 text-primary'}`
                        : `${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
                      }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                    {tab.badge != null && tab.badge > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {tab.badge > 9 ? '9+' : tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Right: Auth */}
            <div className="flex items-center">
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/more')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium min-h-[44px] transition-colors
                    ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  <User className="h-5 w-5" />
                  <span>Account</span>
                </button>
              ) : (
                <Link
                  to="/auth?redirect=/shop"
                  className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium min-h-[44px] transition-colors"
                >
                  <User className="h-5 w-5" />
                  <span>Sign In</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - scrollable */}
      <main className="flex-1 min-h-0 overflow-y-auto">
        <Outlet />
      </main>

      {/* Bottom Tab Bar - mobile only */}
      <nav className="lg:hidden flex-shrink-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700">
        <div className="flex">
          {tabs.map((tab) => {
            const isActive = getActiveTab() === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                className={`flex-1 flex flex-col items-center justify-center py-2 relative transition-colors
                  ${isActive 
                    ? 'text-primary dark:text-blue-400' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
              >
                <div className="relative">
                  {tab.icon}
                  {tab.badge != null && tab.badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                      {tab.badge > 9 ? '9+' : tab.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs mt-1">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Desktop Footer */}
      <footer className={`hidden lg:block flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <p className={`text-center text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            © {new Date().getFullYear()} {courier?.name || 'Shop'}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ShopLayout;
